import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Camera, Upload, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhotoUpload = ({ currentPhotoUrl, onPhotoUpdated, token }) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Seuls les formats JPEG, PNG et WebP sont acceptés');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('La taille du fichier doit être inférieure à 5 Mo');
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        const file = fileInputRef.current?.files?.[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API}/upload/photo`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.success('Photo mise à jour avec succès !');
            onPhotoUpdated(response.data.photo_url);
            setPreview(null);
            fileInputRef.current.value = '';
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
        } finally {
            setUploading(false);
        }
    };

    const cancelPreview = () => {
        setPreview(null);
        fileInputRef.current.value = '';
    };

    const displayUrl = preview || currentPhotoUrl;
    const isExternalUrl = currentPhotoUrl?.startsWith('http');
    const photoSrc = displayUrl 
        ? (isExternalUrl || preview ? displayUrl : `${process.env.REACT_APP_BACKEND_URL}${displayUrl}`)
        : null;

    return (
        <div className="space-y-4" data-testid="photo-upload">
            {/* Photo Preview */}
            <div className="relative w-32 h-32 mx-auto">
                {photoSrc ? (
                    <img
                        src={photoSrc}
                        alt="Photo de profil"
                        className="w-full h-full rounded-xl object-cover border-2 border-border"
                    />
                ) : (
                    <div className="w-full h-full rounded-xl bg-muted flex items-center justify-center border-2 border-dashed border-border">
                        <Camera className="w-8 h-8 text-muted-foreground" />
                    </div>
                )}

                {preview && (
                    <button
                        onClick={cancelPreview}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Upload Controls */}
            <div className="flex flex-col items-center gap-2">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="photo-input"
                />

                {!preview ? (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-full"
                        data-testid="select-photo-btn"
                    >
                        <Camera className="w-4 h-4 mr-2" />
                        {currentPhotoUrl ? 'Changer la photo' : 'Ajouter une photo'}
                    </Button>
                ) : (
                    <Button
                        type="button"
                        onClick={handleUpload}
                        disabled={uploading}
                        className="btn-primary"
                        data-testid="upload-photo-btn"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Téléchargement...
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4 mr-2" />
                                Enregistrer
                            </>
                        )}
                    </Button>
                )}

                <p className="text-xs text-muted-foreground text-center">
                    JPEG, PNG ou WebP • Max 5 Mo
                </p>
            </div>
        </div>
    );
};
